import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, ExternalLink, Eye, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/cn";
import { ApiClientError } from "@/api/client";
import { formatFileSize } from "@/api/clientMaterials";
import {
  KbDuplicateExisting,
  archiveKbItem,
  createKbDraft,
  fetchKbItem,
  fetchKbSection,
  formatKbDate,
  kbFileExt,
  kbFileUrl,
  newKbRequestId,
  pinKbItem,
  publishKbItem,
  restoreKbItem,
  unpinKbItem,
} from "@/api/knowledge";
import { downloadAttachment, openAttachmentInNewTab } from "@/api/taskAttachments";
import { AttachmentViewer } from "@/components/attachments/AttachmentViewer";
import { KbContentHtml } from "./KbContentHtml";
import { KbDuplicateDialog } from "./KbDuplicateDialog";
import { KbErrorBanner, KbErrorCard, KbNotFound, KbRowsSkeleton } from "./kbUi";

/**
 * D-147 W1 S3+S4 — картка матеріалу (visual package v1.2 LOCK, екран ③).
 *
 * Reader (S3): published-зріз, жодних draft-слідів (payload їх не має,
 * AC-KB-01/05). Editor+ (S4): toggle Опублікована/Чернетка · warning-банер
 * rev/автор · state-aware дії ВИКЛЮЧНО з backend allowedActions (P2; unknown
 * reasonCode → «Дія недоступна»): no_draft → «Створити чернетку»; publish =
 * ЛИШЕ owner, справжній disabled+aria поза вкладкою «Чернетка», несе
 * expectedDraftRevision; no_changes → publish відсутній. Publish-дублікат
 * назви → єдиний duplicate-діалог (confirm-override retry, той самий
 * requestId — ledger re-arm-ить failed-рядок).
 */
export function KnowledgeItemPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const id = itemId ?? "";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewerOpen, setViewerOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [publishAsk, setPublishAsk] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishRequestId, setPublishRequestId] = useState(newKbRequestId);
  const [duplicate, setDuplicate] = useState<KbDuplicateExisting | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [archiveAsk, setArchiveAsk] = useState(false);

  const item = useQuery({
    queryKey: ["kbItem", id],
    queryFn: ({ signal }) => fetchKbItem(id, signal),
    enabled: !!id,
    staleTime: 30_000,
    retry: (count, e) => !(e instanceof ApiClientError && e.status === 404) && count < 2,
  });

  const section = useQuery({
    queryKey: ["kbSection", item.data?.sectionId ?? ""],
    queryFn: ({ signal }) => fetchKbSection(item.data?.sectionId ?? "", signal),
    enabled: !!item.data?.sectionId,
    staleTime: 30_000,
  });

  const folderNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of section.data?.folders ?? []) map.set(f.id, f.name);
    return map;
  }, [section.data?.folders]);

  const notFound = item.error instanceof ApiClientError && item.error.status === 404;
  if (notFound) {
    return (
      <Wrap>
        <KbNotFound text="Матеріал недоступний або не існує" />
      </Wrap>
    );
  }

  const data = item.data;
  const pub = data?.published;
  const draft = data?.draft;
  const editorGrade = data?.state !== undefined;
  const hasDraft = !!draft;
  const hasPublished = !!pub;

  // Вкладка: draft-only item → лише «Чернетка»; ?view=draft → чернетка
  const view: "published" | "draft" =
    hasDraft && (!hasPublished || searchParams.get("view") === "draft") ? "draft" : "published";

  const setView = (v: "published" | "draft") => {
    const next = new URLSearchParams(searchParams);
    if (v === "draft") next.set("view", "draft");
    else next.delete("view");
    setSearchParams(next, { replace: true });
  };

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["kbItem", id] });
    void qc.invalidateQueries({ queryKey: ["kbSectionItems"] });
    void qc.invalidateQueries({ queryKey: ["kbHome"] });
  };

  const runAction = (fn: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    void fn()
      .catch((e: unknown) => setActionError(e instanceof Error ? e.message : "Дія не вдалася"))
      .finally(() => setBusy(false));
  };

  const doPublish = (confirmDuplicateTitle: boolean) => {
    if (!draft) return;
    setPublishBusy(true);
    setActionError(null);
    publishKbItem(id, {
      expectedDraftRevision: draft.revision,
      requestId: publishRequestId,
      confirmDuplicateTitle,
    })
      .then(() => {
        setPublishAsk(false);
        setDuplicate(null);
        setPublishRequestId(newKbRequestId());
        setToast("Опубліковано — читачі бачать нову версію.");
        window.setTimeout(() => setToast(null), 4000);
        setView("published");
        invalidate();
      })
      .catch((e: unknown) => {
        setPublishAsk(false);
        if (e instanceof ApiClientError && e.status === 409) {
          const details = (e.details ?? {}) as { code?: string; existing?: KbDuplicateExisting };
          if (details.code === "duplicate_title" || e.code === "duplicate_title") {
            setDuplicate(details.existing ?? { itemId: "", title: "" });
            return;
          }
          if (e.code === "draft_conflict") {
            setActionError("Чернетку змінено після перегляду — оновіть сторінку та повторіть.");
            invalidate();
            return;
          }
        }
        setActionError(e instanceof Error ? e.message : "Не вдалося опублікувати.");
      })
      .finally(() => setPublishBusy(false));
  };

  const ownerName = section.data?.owner?.name ?? "";
  const publish = data?.actions.publish;
  const showPublishBtn =
    editorGrade &&
    data?.state === "draft_exists" &&
    publish &&
    publish.reasonCode !== "NOT_SECTION_OWNER";
  const publishDisabled = view !== "draft" || !publish?.allowed;
  const publishTitle =
    view !== "draft"
      ? "Відкрийте вкладку «Чернетка», щоб опублікувати"
      : publish?.allowed
        ? `Опублікувати цю чернетку (rev ${draft?.revision ?? ""})`
        : publish?.reasonText || "Дія недоступна";

  const headTitle = view === "draft" ? (draft?.title ?? "") : (pub?.title ?? draft?.title ?? "");
  const folderName = (() => {
    const fid = view === "draft" ? draft?.folderId : pub?.folderId;
    return fid ? folderNameById.get(fid) : undefined;
  })();

  return (
    <Wrap>
      {item.isPending ? (
        <KbRowsSkeleton rows={4} />
      ) : item.isError && !data ? (
        <KbErrorCard text="Не вдалося завантажити матеріал" onRetry={() => void item.refetch()} />
      ) : data ? (
        <>
          {item.isError && (
            <KbErrorBanner
              text="Не вдалося оновити матеріал — показано попередні дані."
              onRetry={() => void item.refetch()}
            />
          )}
          {toast && (
            <div className="mb-4 rounded-xl bg-success px-3.5 py-2.5 text-sm font-medium text-success-foreground">
              {toast}
            </div>
          )}
          {actionError && (
            <div role="alert" className="mb-4 rounded-xl bg-destructive-soft px-3.5 py-2.5 text-sm text-destructive-soft-foreground">
              {actionError}
            </div>
          )}

          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <nav className="mb-1 text-xs text-muted-foreground" aria-label="Хлібні крихти">
                <Link to="/knowledge" className="text-primary hover:underline">
                  База знань
                </Link>
                <span> / </span>
                <Link to={`/knowledge/sections/${data.sectionId}`} className="text-primary hover:underline">
                  {data.sectionName}
                </Link>
                {folderName && <span> / {folderName}</span>}
              </nav>
              <h1 className="text-xl font-semibold tracking-tight">{headTitle}</h1>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {[
                  hasPublished ? `опубліковано ${formatKbDate(pub!.lastPublishedAt)}` : "ще не опубліковано",
                  data.owner ? `відповідальний: ${data.owner.name}` : null,
                  data.type === "page" ? "сторінка" : "файл",
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>

            {editorGrade && (
              <div className="flex flex-wrap items-center gap-2">
                {data.actions.createDraft && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      runAction(async () => {
                        await createKbDraft(id);
                        invalidate();
                        navigate(`/knowledge/items/${id}/edit`);
                      })
                    }
                    className="rounded-md border px-3.5 py-2 text-sm font-semibold hover:bg-accent disabled:opacity-50"
                  >
                    Створити чернетку
                  </button>
                )}
                {data.actions.edit && hasDraft && (
                  <Link
                    to={`/knowledge/items/${id}/edit`}
                    className="rounded-md border px-3.5 py-2 text-sm font-semibold hover:bg-accent"
                  >
                    Редагувати чернетку
                  </Link>
                )}
                {showPublishBtn && (
                  <button
                    type="button"
                    disabled={publishDisabled || publishBusy}
                    aria-disabled={publishDisabled || publishBusy}
                    title={publishTitle}
                    onClick={() => setPublishAsk(true)}
                    className="rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Опублікувати
                  </button>
                )}
                {data.actions.pin && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      runAction(async () => {
                        await pinKbItem(id);
                        invalidate();
                      })
                    }
                    className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                  >
                    Закріпити
                  </button>
                )}
                {data.actions.unpin && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      runAction(async () => {
                        await unpinKbItem(id);
                        invalidate();
                      })
                    }
                    className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                  >
                    Відкріпити
                  </button>
                )}
                {data.actions.archive && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setArchiveAsk(true)}
                    className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-destructive disabled:opacity-50"
                  >
                    Архівувати
                  </button>
                )}
                {data.actions.restore && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      runAction(async () => {
                        await restoreKbItem(id);
                        setToast("Відновлено як чернетку.");
                        window.setTimeout(() => setToast(null), 4000);
                        invalidate();
                      })
                    }
                    className="rounded-md border px-3.5 py-2 text-sm font-semibold hover:bg-accent disabled:opacity-50"
                  >
                    Відновити
                  </button>
                )}
              </div>
            )}
          </div>

          {editorGrade && data.status === "archived" && (
            <div className="mt-3 flex items-center gap-2 rounded-xl border bg-muted px-3.5 py-2.5 text-sm text-muted-foreground">
              <TriangleAlert className="h-4 w-4 flex-none" aria-hidden />
              Матеріал в архіві — прихований від читачів у списках, пошуку та відповідях AI. Відновлюється як чернетка.
            </div>
          )}

          {editorGrade && hasDraft && (
            <div className="mt-4">
              {hasPublished && (
                <div
                  className="inline-flex overflow-hidden rounded-lg border"
                  role="tablist"
                  aria-label="Версія матеріалу"
                >
                  {(
                    [
                      ["published", "Опублікована"],
                      ["draft", "Чернетка"],
                    ] as const
                  ).map(([v, label]) => (
                    <button
                      key={v}
                      type="button"
                      role="tab"
                      aria-selected={view === v}
                      onClick={() => setView(v)}
                      className={cn(
                        "px-4 py-1.5 text-[13px] font-semibold",
                        view === v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {data.hasUnpublishedChanges && (
                <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-warning px-3.5 py-2.5 text-sm text-warning-foreground">
                  <TriangleAlert className="h-4 w-4 flex-none" aria-hidden />
                  <span className="min-w-0 flex-1">
                    {hasPublished
                      ? `Чернетку змінено ${formatKbDateTime(draft!.modifiedAt)} · ${draft!.modifiedBy?.name ?? ""} (rev ${draft!.revision}). Читачі бачать версію від ${formatKbDate(pub!.lastPublishedAt)}.`
                      : `Ще не опубліковано. Чернетка · ${formatKbDateTime(draft!.modifiedAt)} · ${draft!.modifiedBy?.name ?? ""} (rev ${draft!.revision}).`}
                  </span>
                  {publish?.reasonCode === "NOT_SECTION_OWNER" && ownerName && (
                    <span className="text-xs">Публікує власник розділу — {ownerName}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {view === "draft" && draft ? (
            <div className="mt-5">
              <div className="mb-3 max-w-[760px] rounded-lg border px-3 py-2 text-xs text-muted-foreground">
                Чернетка · збережено {formatKbDateTime(draft.modifiedAt)} · {draft.modifiedBy?.name ?? ""} · rev{" "}
                {draft.revision} · назва й папка — теж частина чернетки
              </div>
              {data.type === "page" ? (
                <KbContentHtml html={draft.contentHtml ?? ""} />
              ) : (
                <FilePanel
                  data={data}
                  title={draft.title}
                  busy={busy}
                  actionError={null}
                  onView={() => setViewerOpen(true)}
                  onDownload={() => runAction(() => downloadAttachment(kbFileUrl(id), draft.title))}
                  onNewTab={() => runAction(() => openAttachmentInNewTab(kbFileUrl(id)))}
                />
              )}
            </div>
          ) : pub ? (
            <div className="mt-5">
              {data.type === "page" ? (
                <KbContentHtml html={pub.contentHtml ?? ""} />
              ) : (
                <FilePanel
                  data={data}
                  title={pub.title}
                  busy={busy}
                  actionError={actionError}
                  onView={() => setViewerOpen(true)}
                  onDownload={() => runAction(() => downloadAttachment(kbFileUrl(id), pub.title))}
                  onNewTab={() => runAction(() => openAttachmentInNewTab(kbFileUrl(id)))}
                />
              )}
            </div>
          ) : null}

          {viewerOpen && data.type === "file" && (
            <AttachmentViewer
              downloadUrl={kbFileUrl(id)}
              filename={headTitle}
              subtitle={data.file?.originalFilename}
              mime={data.file?.mime ?? undefined}
              onClose={() => setViewerOpen(false)}
            />
          )}

          {publishAsk && (
            <div
              className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
              role="dialog"
              aria-modal="true"
              aria-label="Підтвердження публікації"
            >
              <div className="w-full max-w-md rounded-xl border bg-card p-5 shadow-2xl">
                <div className="text-sm font-semibold">Опублікувати чернетку?</div>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  Читачі побачать нову версію (вкл. назву й папку) одразу. Заміна атомарна.
                </p>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setPublishAsk(false)}
                    className="rounded-md px-3.5 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
                  >
                    Скасувати
                  </button>
                  <button
                    type="button"
                    disabled={publishBusy}
                    onClick={() => doPublish(false)}
                    className="rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                  >
                    {publishBusy ? "Публікуємо…" : "Опублікувати"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {archiveAsk && (
            <div
              className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
              role="dialog"
              aria-modal="true"
              aria-label="Підтвердження архівування"
            >
              <div className="w-full max-w-md rounded-xl border bg-card p-5 shadow-2xl">
                <div className="text-sm font-semibold">Архівувати матеріал?</div>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  Матеріал зникне зі списків, пошуку та відповідей AI. Відновлюється як чернетка.
                </p>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setArchiveAsk(false)}
                    className="rounded-md px-3.5 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
                  >
                    Скасувати
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setArchiveAsk(false);
                      runAction(async () => {
                        await archiveKbItem(id);
                        setToast("Матеріал архівовано.");
                        window.setTimeout(() => setToast(null), 4000);
                        invalidate();
                      });
                    }}
                    className="rounded-md bg-destructive px-3.5 py-2 text-sm font-semibold text-destructive-foreground disabled:opacity-50"
                  >
                    Архівувати
                  </button>
                </div>
              </div>
            </div>
          )}

          {duplicate !== null && (
            <KbDuplicateDialog
              kind="title"
              existing={duplicate.itemId ? duplicate : null}
              confirmLabel="Опублікувати як окремий"
              busy={publishBusy}
              onCancel={() => setDuplicate(null)}
              onConfirm={() => doPublish(true)}
            />
          )}
        </>
      ) : null}
    </Wrap>
  );
}

function FilePanel({
  data,
  title,
  busy,
  actionError,
  onView,
  onDownload,
  onNewTab,
}: {
  data: import("@/api/knowledge").KbItemResponse;
  title: string;
  busy: boolean;
  actionError: string | null;
  onView: () => void;
  onDownload: () => void;
  onNewTab: () => void;
}) {
  return (
    <div className="max-w-[640px] overflow-hidden rounded-xl border bg-card">
      <div className="px-4 py-3 text-sm">
        <span className="font-semibold">{title}</span>{" "}
        <span className="ml-1 inline-flex items-center rounded-full bg-violet px-2 py-0.5 text-[11px] font-semibold text-violet-foreground">
          {[kbFileExt(data.file?.originalFilename), formatFileSize(data.file?.size ?? null)]
            .filter((p) => p && p !== "—")
            .join(" · ")}
        </span>
        <div className="mt-1 text-[11px] text-muted-foreground">
          оригінал: {data.file?.originalFilename}
        </div>
      </div>
      <button
        type="button"
        onClick={onView}
        className="flex h-[110px] w-full items-center justify-center bg-[#525659] text-sm text-[#cbd0d4] hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Eye className="mr-1.5 h-4 w-4" aria-hidden /> Переглянути
      </button>
      {actionError && (
        <p role="alert" className="px-4 pt-2 text-xs text-destructive">
          {actionError}
        </p>
      )}
      <div className="flex gap-2 px-4 py-3">
        <button
          type="button"
          disabled={busy}
          onClick={onDownload}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" aria-hidden /> Завантажити
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onNewTab}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden /> У новій вкладці
        </button>
      </div>
    </div>
  );
}

function Wrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-[1080px]">{children}</div>
    </div>
  );
}

function formatKbDateTime(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return "";
  return (
    d.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    " о " +
    d.toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" })
  );
}
